import { makeAutoObservable, runInAction } from 'mobx'
import { BlogTagsService } from '~/shared/api/services/blog-tags'
import {
  BlogTagApiEntity,
  BlogTagListItem,
} from '~/shared/api/services/blog-tags/types'
import { handleAxiosError } from '~/shared/lib'
import { BlogTagsLoaders } from './types'

const EMPTY_VALUE = '-'

const getTextValue = (value?: string | null) => value?.trim() || EMPTY_VALUE

const normalizeTag = (tag: BlogTagApiEntity): BlogTagListItem => ({
  id: tag.id,
  name: getTextValue(tag.name),
  store: getTextValue(tag.store?.name),
})

export class BlogTagsStore {
  tags: BlogTagListItem[] = []

  loaders: BlogTagsLoaders = {
    tags: false,
    loadMoreTags: false,
  }

  requestSettings = {
    limit: 20,
    offset: 0,
    isAllLoaded: false,
  }

  constructor() {
    makeAutoObservable(this)
  }

  activeLoader = (key: keyof BlogTagsLoaders) => {
    this.loaders = { ...this.loaders, [key]: true }
  }

  closeLoader = (key: keyof BlogTagsLoaders) => {
    this.loaders = { ...this.loaders, [key]: false }
  }

  getTags = async (action?: 'loadMoreTags') => {
    if (action === 'loadMoreTags' && this.requestSettings.isAllLoaded) {
      return
    }

    this.activeLoader(action || 'tags')

    try {
      const params = {
        limit: this.requestSettings.limit,
        offset: action === 'loadMoreTags' ? this.requestSettings.offset : 0,
      }

      const { data } = await BlogTagsService.getTags({ params })
      const normalizedTags = data.map(normalizeTag)

      if (action === 'loadMoreTags') {
        runInAction(() => {
          this.tags = [...this.tags, ...normalizedTags]
        })
      } else {
        runInAction(() => {
          this.tags = normalizedTags
        })
      }

      runInAction(() => {
        this.requestSettings = {
          ...this.requestSettings,
          isAllLoaded: normalizedTags.length < this.requestSettings.limit,
          offset:
            action === 'loadMoreTags'
              ? this.requestSettings.offset + this.requestSettings.limit
              : this.requestSettings.limit,
        }
      })

      return normalizedTags
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader(action || 'tags')
    }
  }
}

export default BlogTagsStore
